import { useEffect, useMemo, useRef, useState } from "react";
import { customFetch } from "@workspace/api-client-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Crown, Check, CreditCard, QrCode, Copy, ArrowLeft, Rocket, Sparkles } from "lucide-react";
import { useLocation } from "wouter";
import { useConfirm } from "@/components/ui/confirm-dialog";
import {
  PLAN_DEFS,
  priceCents,
  formatBRL,
  isPaidPlanId,
  type BillingCycleUI,
  type PaidPlanId,
} from "@/components/PlanPicker";
import {
  isValidCreditCardNumber,
  detectCardBrand,
  isValidExpiryDate,
  isValidHolderName,
  formatCardNumberMask,
  ccvLengthForBrand,
  isValidCpfCnpj,
} from "@/utils/cardValidators";

type PaymentMethod = "credit_card" | "pix";

interface CardSummary {
  id: string;
  creditCardNumber: string;
  creditCardBrand: string;
  isPrimary: boolean;
}

interface UpgradeInfo {
  fromPlan?: string;
  toPlan?: string;
  credit: number;
  firstCharge: number;
  recurring: number;
  extraDays: number;
  nextDueDate: string;
}

interface PixData {
  authorizationId: string;
  encodedImage: string | null;
  payload: string | null;
  expirationDate: string | null;
  upgrade?: UpgradeInfo;
}

// Prévia do upgrade (GET /api/asaas/upgrade-preview): o que o lojista em dia
// paga HOJE pra subir de plano, com o crédito dos dias restantes abatido.
interface UpgradePreview {
  eligible: boolean;
  reason?: string;
  canSubscribe?: boolean;
  currentPlan?: string;
  currentCycle?: string;
  currentNextDueDate?: string | null;
  quote?: {
    credit: number;
    firstCharge: number;
    recurring: number;
    extraDays: number;
    remainingDays: number;
    cycleDays: number;
    periodEnd: string;
    nextDueDate: string;
  };
}

function formatBRDateIso(iso: string | null | undefined): string {
  if (!iso) return "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

// Máscara dual: até 11 dígitos formata como CPF; do 12º em diante vira CNPJ.
function formatCpfCnpj(value: string): string {
  const digits = (value || "").replace(/\D+/g, "").slice(0, 14);
  if (digits.length <= 11) {
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  }
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

export default function Subscribe() {
  const [, setLocation] = useLocation();
  const confirm = useConfirm();
  // Plano escolhido: ?plan=ultra vem dos selos "Disponível no Ultra".
  const [planId, setPlanId] = useState<PaidPlanId>(() => {
    if (typeof window === "undefined") return "profissional";
    const p = new URLSearchParams(window.location.search).get("plan");
    return isPaidPlanId(p) ? p : "profissional";
  });
  const [cycle, setCycle] = useState<BillingCycleUI>(() => {
    if (typeof window === "undefined") return "yearly";
    const param = new URLSearchParams(window.location.search).get("cycle");
    return param === "monthly" ? "monthly" : "yearly";
  });
  const [method, setMethod] = useState<PaymentMethod>("pix");
  const [cards, setCards] = useState<CardSummary[]>([]);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [showNewCardForm, setShowNewCardForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  // "subscribed" = pagou agora; "card-updated" = já estava ativa, só trocou o
  // cartão da renovação; "already-active" = já estava ativa e nada mudou.
  // Antes tudo virava "Assinatura ativada!" — falso nos dois últimos casos.
  const [successKind, setSuccessKind] = useState<
    "subscribed" | "card-updated" | "already-active" | "upgraded" | "upgrade-pending"
  >("subscribed");
  const [updatedCardLast4, setUpdatedCardLast4] = useState<string | null>(null);
  const [upgradeNextDue, setUpgradeNextDue] = useState<string | null>(null);
  const [pixUpgrade, setPixUpgrade] = useState<UpgradeInfo | null>(null);
  const [upgrade, setUpgrade] = useState<UpgradePreview | null>(null);

  const [fullName, setFullName] = useState("");
  const [cpf, setCpf] = useState("");
  const [holderName, setHolderName] = useState("");
  const [number, setNumber] = useState("");
  const [expiryMonth, setExpiryMonth] = useState("");
  const [expiryYear, setExpiryYear] = useState("");
  const [ccv, setCcv] = useState("");
  const [holderNameError, setHolderNameError] = useState<string | null>(null);
  const [numberError, setNumberError] = useState<string | null>(null);
  const [expiryError, setExpiryError] = useState<string | null>(null);
  const [cpfError, setCpfError] = useState<string | null>(null);

  const cpfDigits = cpf.replace(/\D/g, "");
  const cpfValid = isValidCpfCnpj(cpfDigits);

  const numberDigits = number.replace(/\D/g, "");
  const cardBrand = detectCardBrand(numberDigits);
  const ccvMaxLength = ccvLengthForBrand(cardBrand);

  const cardFieldsValid =
    isValidHolderName(holderName) &&
    isValidCreditCardNumber(numberDigits) &&
    cardBrand !== null &&
    isValidExpiryDate(expiryMonth.padStart(2, "0"), expiryYear) &&
    ccv.replace(/\D/g, "").length === ccvMaxLength;

  const [storeInfo, setStoreInfo] = useState<{ name: string; logoUrl: string | null } | null>(null);
  // Token do link compartilhável (/assinar?token=...). Quando presente, a
  // assinatura é feita SEM login (parente/terceiro paga). Sem redirect pro
  // /dashboard no fim (o pagante não tem sessão).
  const [shareToken] = useState<string | null>(() =>
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("token")
      : null,
  );

  const [pixData, setPixData] = useState<PixData | null>(null);
  const [pixCopied, setPixCopied] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const pollingRef = useRef<number | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  const def = PLAN_DEFS[planId];
  const cents = useMemo(() => priceCents(planId, cycle), [planId, cycle]);
  const upgradeQuote = upgrade?.eligible && upgrade.quote ? upgrade.quote : null;
  const isDowngrade = !!upgrade && !upgrade.eligible && upgrade.reason === "downgrade";

  useEffect(() => {
    document.title = `${upgradeQuote ? "Upgrade para o" : "Assinar"} Plano ${PLAN_DEFS[planId].name} - Venda no Zap`;
  }, [planId, upgradeQuote]);

  // Prévia do upgrade só faz sentido logado (via link o pagante não tem
  // sessão); 401/erro = sem prévia, fluxo normal.
  const cycleFromPreview = useRef(false);
  useEffect(() => {
    if (shareToken) {
      setUpgrade(null);
      return;
    }
    let cancelled = false;
    customFetch<UpgradePreview>(`/api/asaas/upgrade-preview?plan=${planId}&cycle=${cycle}`)
      .then((d) => {
        if (cancelled) return;
        setUpgrade(d);
        // Quem está no mensal faz upgrade no mensal por padrão (a URL pode
        // forçar o ciclo; a prévia só decide uma vez).
        if (!cycleFromPreview.current && d?.eligible && d.currentCycle === "MONTHLY") {
          const param = new URLSearchParams(window.location.search).get("cycle");
          if (!param) setCycle("monthly");
          cycleFromPreview.current = true;
        }
      })
      .catch(() => {
        if (!cancelled) setUpgrade(null);
      });
    return () => {
      cancelled = true;
    };
  }, [planId, cycle, shareToken]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const token = new URLSearchParams(window.location.search).get("token");
    if (!token) return;
    let cancelled = false;
    customFetch<{ store: { name: string; logoUrl: string | null } }>(
      `/api/subscribe-info?token=${encodeURIComponent(token)}`,
    )
      .then((data) => {
        if (cancelled) return;
        if (data?.store) setStoreInfo(data.store);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    customFetch<{ cards: CardSummary[] }>("/api/asaas/credit-cards")
      .then((data) => {
        if (cancelled) return;
        setCards(data.cards || []);
        const primary = data.cards?.find((c) => c.isPrimary) ?? data.cards?.[0];
        setSelectedCardId(primary?.id ?? null);
        if (!primary) setShowNewCardForm(true);
      })
      .catch(() => {
        if (!cancelled) setShowNewCardForm(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    customFetch<{ user: { firstName?: string; lastName?: string; name?: string; cpf?: string | null } }>(
      "/api/auth/me",
    )
      .then((data) => {
        if (cancelled) return;
        const u = data.user || {};
        const composed = u.name || [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
        setFullName((prev) => prev || composed || "");
        setHolderName((prev) => prev || (composed ? composed.toUpperCase() : ""));
        setCpf((prev) => prev || formatCpfCnpj(u.cpf ?? ""));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!pixData?.expirationDate) return;
    const exp = new Date(pixData.expirationDate).getTime();
    const tick = () => {
      const remaining = Math.max(0, Math.floor((exp - Date.now()) / 1000));
      setSecondsLeft(remaining);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [pixData?.expirationDate]);

  useEffect(() => {
    if (!pixData?.authorizationId) return;
    const poll = async () => {
      try {
        const data = await customFetch<{ status: "pending" | "active" | "expired" }>(
          `/api/asaas/payment-status/${pixData.authorizationId}`,
        );
        if (data.status === "active") {
          setSuccess(true);
          if (pollingRef.current) window.clearInterval(pollingRef.current);
          // Via link compartilhável o pagante não tem sessão — não manda pro
          // /dashboard (cairia no login). Só mostra a tela de sucesso.
          if (!shareToken) {
            setTimeout(() => {
              window.location.href = "/dashboard";
            }, 2000);
          }
        }
      } catch {}
    };
    pollingRef.current = window.setInterval(poll, 5000);
    return () => {
      if (pollingRef.current) window.clearInterval(pollingRef.current);
    };
  }, [pixData?.authorizationId]);

  const handleCancelPix = async () => {
    if (cancelling) return;
    if (!(await confirm({
      title: "Cancelar este pagamento PIX?",
      description: "O QR Code será invalidado.",
      confirmLabel: "Cancelar PIX",
      cancelLabel: "Voltar",
    }))) return;
    setCancelling(true);
    try {
      await customFetch("/api/asaas/cancel-pix", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ authorizationId: pixData?.authorizationId ?? "" }),
      });
    } catch {}
    if (pollingRef.current) window.clearInterval(pollingRef.current);
    setPixData(null);
    setSecondsLeft(null);
    setCancelling(false);
  };

  const handleCopyPix = async () => {
    if (!pixData?.payload) return;
    try {
      await navigator.clipboard.writeText(pixData.payload);
      setPixCopied(true);
      setTimeout(() => setPixCopied(false), 3000);
    } catch {
      setError("Não foi possível copiar o código.");
    }
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      if (method === "credit_card") {
        if (!fullName.trim() || !cpf.trim()) {
          setError("Informe seu nome completo e CPF ou CNPJ para continuar.");
          setSubmitting(false);
          return;
        }
        if (!cpfValid) {
          setError("CPF/CNPJ inválido. Verifique os números digitados.");
          setSubmitting(false);
          return;
        }
        let cardId = selectedCardId;
        const needCard = showNewCardForm || !cardId;
        if (needCard) {
          if (!holderName || !number || !expiryMonth || !expiryYear || !ccv) {
            setError("Preencha todos os dados do cartão.");
            setSubmitting(false);
            return;
          }
          if (!cardFieldsValid) {
            setError("Verifique os dados do cartão.");
            setSubmitting(false);
            return;
          }
        }
        if (shareToken) {
          // Via link (sem login): manda o cartão cru direto pro subscribe — não
          // dá pra pré-tokenizar via /credit-cards (exige sessão).
          await customFetch("/api/asaas/subscribe-credit-card", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              planName: planId,
              cycle,
              card: {
                holderName,
                number: numberDigits,
                expiryMonth: expiryMonth.padStart(2, "0"),
                expiryYear: `20${expiryYear}`,
                ccv,
              },
              fullName,
              cpf: cpfDigits,
              token: shareToken,
            }),
          });
        } else {
          if (needCard) {
            const newCard = await customFetch<{ card: CardSummary }>("/api/asaas/credit-cards", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                holderName,
                number: numberDigits,
                expiryMonth: expiryMonth.padStart(2, "0"),
                expiryYear: `20${expiryYear}`,
                ccv,
                cpf: cpfDigits,
                fullName,
              }),
            });
            cardId = newCard.card.id;
          }
          const result = await customFetch<{
            ok: boolean;
            alreadyActive?: boolean;
            cardUpdated?: boolean;
            card?: { creditCardNumber: string; creditCardBrand: string };
            upgrade?: { applied: boolean; pending?: boolean; plan: string; nextDueDate?: string };
          }>("/api/asaas/subscribe-credit-card", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ planName: planId, cycle, creditCardId: cardId, fullName, cpf: cpfDigits }),
          });
          if (result?.alreadyActive) {
            setSuccessKind(result.cardUpdated ? "card-updated" : "already-active");
            setUpdatedCardLast4(result.card?.creditCardNumber ?? null);
          } else if (result?.upgrade?.applied) {
            setSuccessKind("upgraded");
            setUpgradeNextDue(result.upgrade.nextDueDate ?? null);
          } else if (result?.upgrade?.pending) {
            setSuccessKind("upgrade-pending");
          }
        }
        setSuccess(true);
        if (!shareToken) {
          setTimeout(() => {
            window.location.href = "/dashboard";
          }, 2500);
        }
      } else {
        if (!fullName.trim() || !cpf.trim()) {
          setError("Informe seu nome completo e CPF ou CNPJ para gerar o PIX.");
          setSubmitting(false);
          return;
        }
        if (!cpfValid) {
          setError("CPF/CNPJ inválido. Verifique os números digitados.");
          setSubmitting(false);
          return;
        }
        const data = await customFetch<PixData & { alreadyActive?: boolean }>("/api/asaas/subscribe-pix", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ planName: planId, cycle, fullName, cpf: cpfDigits, token: shareToken }),
        });
        if (data.alreadyActive) {
          // Já tem assinatura ativa paga — não gera QR novo (evita duplo-pagto).
          setSuccessKind("already-active");
          setSuccess(true);
          if (!shareToken) {
            setTimeout(() => {
              window.location.href = "/dashboard";
            }, 1500);
          }
        } else {
          setPixData(data);
          setPixUpgrade(data.upgrade ?? null);
        }
      }
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message;
      setError(msg || "Não foi possível processar o pagamento.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-[100svh] flex items-center justify-center bg-muted/30 p-4">
        <Card className="max-w-md p-6 text-center space-y-3">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-xl font-bold font-display">
            {successKind === "card-updated"
              ? "Cartão atualizado!"
              : successKind === "already-active"
                ? "Sua assinatura já está ativa"
                : successKind === "upgrade-pending"
                  ? "Pagamento em análise"
                  : successKind === "upgraded" || pixUpgrade
                    ? `Plano ${def.name} ativado!`
                    : "Assinatura ativada!"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {successKind === "card-updated"
              ? `A próxima renovação será cobrada no cartão${updatedCardLast4 ? ` final ${updatedCardLast4}` : " novo"}.`
              : successKind === "upgrade-pending"
                ? "Assim que a operadora confirmar, seu plano muda sozinho."
                : successKind === "upgraded" || pixUpgrade
                  ? `Próxima cobrança em ${formatBRDateIso(upgradeNextDue ?? pixUpgrade?.nextDueDate)}. Redirecionando para o painel…`
                  : "Redirecionando para o painel…"}
          </p>
        </Card>
      </div>
    );
  }

  if (pixData) {
    const minutes = secondsLeft != null ? Math.floor(secondsLeft / 60) : null;
    const seconds = secondsLeft != null ? secondsLeft % 60 : null;
    return (
      <div className="min-h-[100svh] bg-muted/30 py-8 px-4 flex items-center justify-center">
        <Card className="max-w-md w-full p-6 space-y-4 text-center">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
            <QrCode className="w-7 h-7 text-primary" />
          </div>
          <h1 className="text-xl font-bold font-display">Pague com PIX</h1>
          <p className="text-sm text-muted-foreground">
            Escaneie o QR Code ou copie o código abaixo no aplicativo do seu banco.
          </p>
          {pixData.encodedImage && (
            <img
              src={`data:image/png;base64,${pixData.encodedImage}`}
              alt="QR Code PIX"
              className="w-64 h-64 mx-auto bg-white p-2 rounded-lg border"
            />
          )}
          {pixData.payload && (
            <div className="space-y-2">
              <div className="bg-muted rounded-lg p-3 text-xs break-all text-left font-mono">
                {pixData.payload}
              </div>
              <Button onClick={handleCopyPix} className="w-full cursor-pointer" variant="outline">
                <Copy className="w-4 h-4 mr-2" />
                {pixCopied ? "Código copiado!" : "Copiar código PIX"}
              </Button>
            </div>
          )}
          {minutes != null && seconds != null && (
            <p className="text-xs text-muted-foreground">
              Expira em {String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")}
            </p>
          )}
          <p className="text-[11px] text-muted-foreground">
            Aguardando confirmação do pagamento…
          </p>
          <Button
            type="button"
            variant="ghost"
            className="w-full cursor-pointer text-destructive hover:text-destructive"
            onClick={handleCancelPix}
            disabled={cancelling}
          >
            {cancelling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Cancelar pagamento
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-[100svh] bg-muted/30 py-8 px-4">
      <div className="max-w-md mx-auto space-y-5">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="cursor-pointer -ml-2"
          onClick={() => setLocation("/configuracoes")}
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Voltar para configurações
        </Button>
        <div className="flex flex-col items-center text-center gap-3">
          {storeInfo?.logoUrl ? (
            <img
              src={storeInfo.logoUrl}
              alt={storeInfo.name}
              className="w-16 h-16 rounded-full object-cover border"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Crown className="w-8 h-8 text-primary" />
            </div>
          )}
          {storeInfo?.name && (
            <p className="text-sm font-medium text-muted-foreground">{storeInfo.name}</p>
          )}
          <h1 className="text-2xl font-bold font-display">
            {upgradeQuote ? `Upgrade para o Plano ${def.name}` : `Assinar Plano ${def.name}`}
          </h1>
        </div>

        <Card className="p-5 space-y-4">
          <div className="flex justify-center">
            <div className="inline-flex bg-muted rounded-full p-1 text-xs font-medium" role="tablist" aria-label="Plano">
              {(["profissional", "ultra"] as PaidPlanId[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPlanId(p)}
                  className={`px-4 py-1.5 rounded-full transition-colors cursor-pointer inline-flex items-center gap-1 ${planId === p ? "bg-card shadow-sm text-foreground" : "text-muted-foreground"}`}
                  data-testid={`plan-${p}`}
                >
                  {p === "ultra" && <Rocket className="w-3 h-3" />}
                  {PLAN_DEFS[p].name}
                </button>
              ))}
            </div>
          </div>
          {isDowngrade && (
            <p className="text-xs text-center text-muted-foreground">
              Sua loja já está no plano {PLAN_DEFS[isPaidPlanId(upgrade?.currentPlan) ? upgrade.currentPlan : "ultra"].name}.
            </p>
          )}
          <div className="flex flex-col items-center gap-1">
            <span
              className={`text-xs font-bold text-accent leading-none transition-opacity ${cycle === "yearly" ? "opacity-100" : "opacity-0"}`}
              aria-hidden={cycle !== "yearly"}
            >
              R${Math.round((def.monthlyCents * 12 - def.yearlyCents) / 100)} desconto!
            </span>
            <div className="inline-flex bg-muted rounded-full p-1 text-xs font-medium">
              <button
                type="button"
                onClick={() => setCycle("monthly")}
                className={`px-4 py-1.5 rounded-full transition-colors cursor-pointer ${cycle === "monthly" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground"}`}
              >
                Mensal
              </button>
              <button
                type="button"
                onClick={() => setCycle("yearly")}
                className={`px-4 py-1.5 rounded-full transition-colors cursor-pointer ${cycle === "yearly" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground"}`}
              >
                Anual
              </button>
            </div>
          </div>

          {upgradeQuote ? (
            <div
              className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm space-y-1.5 text-center dark:bg-amber-950/30 dark:border-amber-800"
              data-testid="upgrade-quote"
            >
              <p className="text-muted-foreground">
                Você já pagou o {PLAN_DEFS[isPaidPlanId(upgrade?.currentPlan) ? upgrade.currentPlan : "profissional"].name} até{" "}
                {formatBRDateIso(upgradeQuote.periodEnd)} — crédito de {formatBRL(Math.round(upgradeQuote.credit * 100))} pelos{" "}
                {upgradeQuote.remainingDays} dias restantes.
              </p>
              <p>
                <span className="text-xs text-muted-foreground">Hoje</span>
                <br />
                <span className="text-3xl font-bold text-primary">{formatBRL(Math.round(upgradeQuote.firstCharge * 100))}</span>
              </p>
              <p className="text-muted-foreground">
                Depois {formatBRL(Math.round(upgradeQuote.recurring * 100))}/{cycle === "yearly" ? "ano" : "mês"} a partir de{" "}
                {formatBRDateIso(upgradeQuote.nextDueDate)}
                {upgradeQuote.extraDays > 0 ? ` (inclui ${upgradeQuote.extraDays} dias do seu crédito)` : ""}.
              </p>
            </div>
          ) : (
            <div className="text-center">
              <span className="text-3xl font-bold text-primary">{formatBRL(cents)}</span>
              <span className="text-sm text-muted-foreground">/{cycle === "yearly" ? "ano" : "mês"}</span>
            </div>
          )}

          <ul className="space-y-1.5">
            {def.features.map((f) => (
              <li key={f} className="text-sm flex items-start gap-1.5">
                <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                <span>{f}</span>
              </li>
            ))}
            {def.comingSoon?.map((f) => (
              <li key={f} className="text-sm flex items-start gap-1.5 text-muted-foreground" data-testid="plan-coming-soon">
                <Sparkles className="w-4 h-4 text-accent/70 flex-shrink-0 mt-0.5" />
                <span>
                  {f}{" "}
                  <span className="inline-block align-middle rounded-full border border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800 px-1.5 text-[10px] font-semibold leading-4">Em breve</span>
                </span>
              </li>
            ))}
            {def.comingSoon && def.comingSoon.length > 0 && (
              <li className="text-sm text-muted-foreground pl-[22px]">E mais novidades a caminho…</li>
            )}
          </ul>
        </Card>

        <Card className="p-5 space-y-4">
          <div className="space-y-3">
            <h2 className="font-semibold text-sm">Dados pessoais</h2>
            <div>
              <Label htmlFor="fullName" className="text-xs">Nome completo</Label>
              <Input
                id="fullName"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Seu nome completo"
                autoComplete="name"
              />
            </div>
            <div>
              <Label htmlFor="cpf" className="text-xs">CPF ou CNPJ</Label>
              <Input
                id="cpf"
                value={cpf}
                onChange={(e) => {
                  setCpf(formatCpfCnpj(e.target.value));
                  setCpfError(null);
                }}
                onBlur={() => {
                  if (cpf && !cpfValid) {
                    setCpfError("CPF/CNPJ inválido. Verifique os números digitados.");
                  }
                }}
                placeholder="CPF ou CNPJ"
                inputMode="numeric"
                autoComplete="off"
                maxLength={18}
              />
              {cpfError && <p className="text-[11px] text-destructive mt-1">{cpfError}</p>}
            </div>
          </div>

          <h2 className="font-semibold text-sm">Forma de pagamento</h2>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setMethod("pix")}
              className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 cursor-pointer transition-colors ${method === "pix" ? "border-primary bg-primary/5" : "border-border"}`}
            >
              <QrCode className="w-5 h-5" />
              <span className="text-xs font-medium">PIX</span>
            </button>
            <button
              type="button"
              onClick={() => setMethod("credit_card")}
              className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 cursor-pointer transition-colors ${method === "credit_card" ? "border-primary bg-primary/5" : "border-border"}`}
            >
              <CreditCard className="w-5 h-5" />
              <span className="text-xs font-medium">Cartão</span>
            </button>
          </div>

          {method === "credit_card" && (
            <div className="space-y-3">
              {cards.length > 0 && !showNewCardForm && (
                <div className="space-y-2">
                  {cards.map((card) => (
                    <label
                      key={card.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${selectedCardId === card.id ? "border-primary bg-primary/5" : "border-border"}`}
                    >
                      <input
                        type="radio"
                        name="card"
                        checked={selectedCardId === card.id}
                        onChange={() => setSelectedCardId(card.id)}
                        className="cursor-pointer"
                      />
                      <CreditCard className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">
                        {card.creditCardBrand} •••• {card.creditCardNumber.slice(-4)}
                      </span>
                      {card.isPrimary && (
                        <span className="ml-auto text-[10px] font-bold uppercase text-primary">Principal</span>
                      )}
                    </label>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full cursor-pointer"
                    onClick={() => setShowNewCardForm(true)}
                  >
                    Usar outro cartão
                  </Button>
                </div>
              )}

              {(showNewCardForm || cards.length === 0) && (
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="number" className="text-xs">Número do cartão</Label>
                    <div className="relative">
                      <Input
                        id="number"
                        value={formatCardNumberMask(number)}
                        onChange={(e) => {
                          setNumber(e.target.value.replace(/\D/g, "").slice(0, 19));
                          setNumberError(null);
                        }}
                        onBlur={() => {
                          if (number && !isValidCreditCardNumber(numberDigits)) {
                            setNumberError("Número do cartão inválido");
                          }
                        }}
                        placeholder="0000 0000 0000 0000"
                        inputMode="numeric"
                        pattern="[0-9 ]*"
                        autoComplete="cc-number"
                      />
                      {cardBrand && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-muted-foreground">
                          {cardBrand}
                        </span>
                      )}
                    </div>
                    {numberError && <p className="text-[11px] text-destructive mt-1">{numberError}</p>}
                  </div>
                  <div>
                    <Label htmlFor="holderName" className="text-xs">Nome impresso no cartão</Label>
                    <Input
                      id="holderName"
                      value={holderName}
                      onChange={(e) => {
                        const filtered = e.target.value.replace(/[^a-zA-ZÀ-ÿ\s]/g, "").toUpperCase();
                        setHolderName(filtered);
                        setHolderNameError(null);
                      }}
                      onBlur={() => {
                        if (holderName && !isValidHolderName(holderName)) {
                          setHolderNameError("Nome inválido. Digite o nome como está no cartão.");
                        }
                      }}
                      placeholder="NOME COMPLETO"
                      autoComplete="cc-name"
                    />
                    {holderNameError && <p className="text-[11px] text-destructive mt-1">{holderNameError}</p>}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label htmlFor="expiryMonth" className="text-xs">Mês</Label>
                      <Input
                        id="expiryMonth"
                        value={expiryMonth}
                        onChange={(e) => {
                          setExpiryMonth(e.target.value.replace(/\D/g, "").slice(0, 2));
                          setExpiryError(null);
                        }}
                        onBlur={() => {
                          if (
                            expiryMonth &&
                            expiryYear.length === 2 &&
                            !isValidExpiryDate(expiryMonth.padStart(2, "0"), expiryYear)
                          ) {
                            setExpiryError("Data de validade inválida");
                          }
                        }}
                        placeholder="MM"
                        maxLength={2}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        autoComplete="cc-exp-month"
                      />
                    </div>
                    <div>
                      <Label htmlFor="expiryYear" className="text-xs">Ano</Label>
                      <Input
                        id="expiryYear"
                        value={expiryYear}
                        onChange={(e) => {
                          setExpiryYear(e.target.value.replace(/\D/g, "").slice(0, 2));
                          setExpiryError(null);
                        }}
                        onBlur={() => {
                          if (
                            expiryYear.length === 2 &&
                            expiryMonth &&
                            !isValidExpiryDate(expiryMonth.padStart(2, "0"), expiryYear)
                          ) {
                            setExpiryError("Data de validade inválida");
                          }
                        }}
                        placeholder="AA"
                        maxLength={2}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        autoComplete="cc-exp-year"
                      />
                    </div>
                    <div>
                      <Label htmlFor="ccv" className="text-xs">CCV</Label>
                      <Input
                        id="ccv"
                        value={ccv}
                        onChange={(e) => setCcv(e.target.value.replace(/\D/g, "").slice(0, ccvMaxLength))}
                        placeholder={ccvMaxLength === 4 ? "0000" : "000"}
                        maxLength={ccvMaxLength}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        autoComplete="cc-csc"
                      />
                    </div>
                  </div>
                  {expiryError && <p className="text-[11px] text-destructive">{expiryError}</p>}
                  {cards.length > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full cursor-pointer"
                      onClick={() => setShowNewCardForm(false)}
                    >
                      Cancelar
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {method === "pix" && (() => {
            const next = new Date();
            if (cycle === "yearly") {
              next.setFullYear(next.getFullYear() + 1);
            } else {
              next.setMonth(next.getMonth() + 1);
            }
            const highlight =
              cycle === "yearly"
                ? String(next.getFullYear())
                : next.toLocaleDateString("pt-BR", { month: "long" });
            return (
              <div className="rounded-lg bg-muted p-3 text-sm text-center space-y-2">
                <p>
                  Você receberá um QR Code para pagar{" "}
                  {formatBRL(upgradeQuote ? Math.round(upgradeQuote.firstCharge * 100) : cents)} agora.
                </p>
                <p>
                  A próxima cobrança será em{" "}
                  <strong>{upgradeQuote ? formatBRDateIso(upgradeQuote.nextDueDate) : highlight}</strong>
                </p>
              </div>
            );
          })()}

          {error && <p className="text-xs text-destructive text-center">{error}</p>}

          <Button
            className="w-full cursor-pointer"
            disabled={
              submitting ||
              isDowngrade ||
              !cpfValid ||
              !fullName.trim() ||
              (method === "credit_card" && (showNewCardForm || cards.length === 0) && !cardFieldsValid)
            }
            onClick={handleSubmit}
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Crown className="w-4 h-4 mr-2" />}
            {upgradeQuote
              ? method === "credit_card"
                ? "Fazer upgrade agora"
                : "Gerar PIX do upgrade"
              : method === "credit_card"
                ? "Assinar agora"
                : "Gerar PIX"}
          </Button>
        </Card>
      </div>
    </div>
  );
}
